import React, { useState, useRef, useEffect,useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import Select from "react-select";
// import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import CustomFieldBuilder from "components/CustomFieldBuilder";
import { Brand, Category, CustomField, License, Office, Agent, RotationalProducts, serializedUnits, Warehouse } from "types";
import ro from "assets/libs/moment/src/locale/ro";
import { getAuthCookie } from "../../utils/authCookies";

type Attribute = {
  id: string;
  name: string;
  values: string[];
  description: string;
  status: string;
};
const SignageViewRotationalProduct = () => {
  const { productId } = useParams<{ productId: string }>();
  const [rotationalProducts, setRotationProducts] = useState<RotationalProducts>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [formSubmitting, setFormSubmitting] = useState(false);
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const [preview, setPreview] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Determine the base route based on current location
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';
  const [brokerageFirms, setBrokerageFirms] = useState<License[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [customFields, setCustomFields] = useState<CustomField[] | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadedFileMeta, setUploadedFileMeta] = useState<any[]>([]);
  const [previewFiles, setPreviewFiles] = useState<{ file: File; url: string }[]>([]);
  const [options, setOptions] = useState<
    { attribute: string; value: string }[]
  >([]);

  const [selectedBrokerageFirm, setSelectedBrokerageFirm] = useState<{
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  } | null>(null);
  const [selectedOffice, setSelectedOffice] = useState<{
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  } | null>(null);

  const [selectedAgent, setSelectedAgent] = useState<{
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  } | null>(null);

  const [selecteWarehouse, setSelectedWarehouse] = useState<{
    value: string;
    label: string;
  } | null>(null);

  const [agentLinkedProducts, setAgentagentLinkedProducts] = useState<
    {
      agentId: string;
      warehouseId: string;
      allocatedQty: string;
      availableQty: number;
    }[]
  >([]);

  const [officeLinkedProducts, setOfficeLinkedProducts] = useState<
    {
      officeId: string;
      warehouseId: string;
      allocatedQty: string;
      availableQty: number;
    }[]
  >([]);

  const [editableWarehouses, setEditableWarehouses] = useState<
    {
      warehouseId: string;
      label: string;
      // bin_location: string;
      available: number;
    }[]
  >([]);

  const [warehouseTotals, setWarehouseTotals] = useState({
    total: 0,
    serialized: 0,
    available: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const totalPages = Math.ceil(warehouses.length / itemsPerPage);

  // Get current paginated data
  const currentWarehouses = warehouses.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );


  useEffect(() => {
    const fetchRotationProducts = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/signage/${userId}/rotational-products/${productId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const data = await response.json();
        console.log("Rotational Products:", data);
        setRotationProducts(data);
        setUploadedFileMeta(data.rotational_product.images)
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchRotationProducts();
  }, [token]);


  useEffect(() => {
    const fetchBrokerageFirms = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/brokerage-firms`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const data = await response.json();
        console.log("fetchedBrokerageFirms", data);
        setBrokerageFirms(data);
        // toast.success("data);

        if (!response.ok) {
          // throw new Error("Failed to fetch agents");
          toast.error(data.error || "Failed to fetch firms");
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchBrokerageFirms();
  }, [token]);

  const brokerageOptions = brokerageFirms?.map((brokerage) => ({
    value: brokerage.id,
    label:
      brokerage?.firmDetails?.companyName + "(" + brokerage?.firstName + ")",
    firstName: brokerage?.firstName,
    lastName: brokerage?.lastName,
    email: brokerage?.email,
    phone: brokerage?.phone1,
  }));

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/brands`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        setBrands(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchBrands();
  }, [token]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/categories`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        setCategories(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [token]);

  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/warehouses`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        console.log("fetchWarehouses Data :", data)
        setWarehouses(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchWarehouses();
  }, [token]);

  useEffect(() => {
    const fetchAttributes = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/product-attributes`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        setAttributes(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchAttributes();
  }, [token]);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const uploadFile = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("uploadDir", "rotational_products");

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${process.env.REACT_APP_API_URL}/images/upload`, true);
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);

      xhr.onload = () => {
        console.log("xhr:", xhr);
        if (xhr.readyState === 4) {
          const response = JSON.parse(xhr.responseText);
          console.log("upload response:", response);

          // Construct the full image URL using the base URL and `path` field
          const imageUrl = `${response.path}`;
          resolve(imageUrl);
        } else {
          reject(new Error("Failed to upload file"));
        }
      };

      xhr.onerror = () => reject(new Error("Network error during file upload"));
      xhr.send(formData);
    });
  };

  const handleAddOption = () => {
    const { attribute, parameter } = formik.values;

    if (!attribute || !parameter) {
      alert("Please select both an attribute and a value.");
      return;
    }

    const isDuplicate = options.some(
      (option) => option.attribute === attribute && option.value === parameter
    );
    if (isDuplicate) {
      alert("This combination already exists.");
      return;
    }

    setOptions((prev) => [...prev, { attribute, value: parameter }]);
    // toast.success("Attribute-Value pair added successfully!");

    formik.setFieldValue("attribute", "");
    formik.setFieldValue("parameter", "");
  };

  // Remove an option
  const handleRemoveOption = (index: number) => {
    setOptions((prev) => prev?.filter((_, i) => i !== index));
  };

  async function Imageuploads() {
    try {
      const uploads = await Promise.all(
        previewFiles.map(async ({ file }) => {
          const path = await uploadFile(file);
          return {
            alt_text: file.name,
            url: path,
          };
        })
      );

      const allFiles = [...uploadedFileMeta, ...uploads];
      const uniqueByName = allFiles.filter(
        (file, index, self) =>
          index === self.findIndex((f) => f.alt_text === file.alt_text)
      );

      setUploadedFileMeta(uniqueByName);
      setPreviewFiles([]);
      // toast.success("Files uploaded successfully!");

      return uniqueByName;  // ✅ send result back to onSubmit
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Failed to upload files.");
      throw err;  // ✅ rethrow to stop onSubmit if upload fails
    }
  }


  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: rotationalProducts?.rotational_product?.name || "",
      sku: rotationalProducts?.rotational_product?.sku || "",
      description: rotationalProducts?.rotational_product?.description || "",
      brand: rotationalProducts?.rotational_product?.brand || "",
      category: rotationalProducts?.rotational_product?.category || "",
      item_condition: rotationalProducts?.rotational_product?.item_condition || "",
      condition_note: "",
      purchase_price: String(rotationalProducts?.rotational_product?.purchase_price || ""),
      retail_price: String(rotationalProducts?.rotational_product?.retail_price || ""),
      length: String(rotationalProducts?.rotational_product?.package_dimensions?.length || ""),
      height: String(rotationalProducts?.rotational_product?.package_dimensions?.height || ""),
      width: String(rotationalProducts?.rotational_product?.package_dimensions?.width || ""),
      weight: String(rotationalProducts?.rotational_product?.package_dimensions?.weight || ""),
      threshold_qty: String(rotationalProducts?.rotational_product?.threshold_qty || ""),
      attribute: "",
      parameter: "",
      notes: "",
      image: null,
      statuss: rotationalProducts?.rotational_product?.status || "",
      warehouse: "",
      allocated_qty: 0,
      available_qty: 0,
      warehouseId: "",
      linked_agent: "",
      select_user: "",
      select_warehouse_id: "",
      serialize_quantity: "",
      serial_prefix: "",
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .min(2, "Name must be at least 2 characters long")
        .max(250, "Name cannot be longer than 50 characters")
        .required("Name is required"),
      sku: Yup.string().required("SKU is required"),
      // purchase_price: Yup.number().required("Purchase price is required"),
      retail_price: Yup.number().required("Retail price is required"),
      // statuss: Yup.string().required("Status is required"),
    }),
    onSubmit: async (values) => {
      console.log("Submitting form...", values);
      setFormSubmitting(true);
      try {
        const uploadedImages = await Imageuploads();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/signage/${userId}/rotational-products/${productId}`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`, // Token already available
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              rotational_product: {
                name: values.name,
                sku: values.sku,
                brand: values.brand,
                category: values.category,
                description: values.description,
                item_condition: values.item_condition,
                purchase_price: Number(values.purchase_price),
                retail_price: Number(values.retail_price),
                package_dimensions: {
                  length: Number(values.length) || 0,
                  width: Number(values.width) || 0,
                  height: Number(values.height) || 0,
                  weight: Number(values.weight) || 0,
                  weight_type: "kg",
                },
                variants: [],
                is_rotational: true,
                linked_mode: "none",
                locked_status: "none",
                product_location_status: "none",
                current_status: "none",
                status: values.statuss,
              },
              warehouse_details: editableWarehouses?.map((item) => ({
                warehouse: warehouses.find((w) => w._id === item.warehouseId),
                // bin_location: item.bin_location,
                available: item.available,
                allocated: 0,
                remaining: item.available,
              })),
              total_quantity: editableWarehouses.reduce(
                (acc, item) => acc + Number(item.available || 0),
                0
              ),
            }),
          }
        );

        const result = await response.json();
        if (!response.ok) {
          console.error("API Error:", result.error);
          toast.error(result.error);
        }

        console.log("API Success:", result);
        navigate(`${baseRoute}/rotationalProducts`, {
          state: { successMessage: "Product added successfully!" },
        });
        // You can reset the form or redirect here
      } catch (error) {
        console.error("Fetch Error:", error);
      } finally {
        setFormSubmitting(false);
      }
    },
  });

  const addAgentLinkedProduct = () => {
    setAgentagentLinkedProducts([
      ...agentLinkedProducts,
      {
        agentId: "",
        warehouseId: "",
        allocatedQty: "",
        availableQty: 0,
      },
    ]);
  };

  const removeAgentLinkedProduct = (index: number) => {
    const updated = [...agentLinkedProducts];
    updated.splice(index, 1);
    setAgentagentLinkedProducts(updated);
  };

  const handleAgentChange = (index: number, selected: any) => {
    const updated = [...agentLinkedProducts];
    updated[index].agentId = selected.value;
    setAgentagentLinkedProducts(updated);
  };

  const handleAgentWarehouseChange = (index: number, selected: any) => {
    const updated = [...agentLinkedProducts];
    updated[index].warehouseId = selected.value;
    setAgentagentLinkedProducts(updated);
  };

  const handleAgentAvailableChange = (index: number, value: number) => {
    const updated = [...agentLinkedProducts];
    updated[index].availableQty = value;
    setAgentagentLinkedProducts(updated);
  };

  const addOfficeLinkedProduct = () => {
    setOfficeLinkedProducts([
      ...officeLinkedProducts,
      {
        officeId: "",
        warehouseId: "",
        allocatedQty: "",
        availableQty: 0,
      },
    ]);
  };

  const removeOfficeLinkedProduct = (index: number) => {
    const updated = [...officeLinkedProducts];
    updated.splice(index, 1);
    setOfficeLinkedProducts(updated);
  };

  const handleOfficeChange = (index: number, selected: any) => {
    const updated = [...officeLinkedProducts];
    updated[index].officeId = selected.value;
    setOfficeLinkedProducts(updated);
  };

  const handleOfficeWarehouseChange = (index: number, selected: any) => {
    const updated = [...officeLinkedProducts];
    updated[index].warehouseId = selected.value;
    setOfficeLinkedProducts(updated);
  };

  const handleOfficeAvailableChange = (index: number, value: number) => {
    const updated = [...officeLinkedProducts];
    updated[index].availableQty = value;
    setOfficeLinkedProducts(updated);
  };

  const warehouseOptions = warehouses?.map((warehouse) => ({
    value: warehouse?._id,
    label: warehouse?.label,
  }));

  const allocateQuantity = async (inventorySource: string) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/signage/${userId}/rotational-products/${productId}/assign`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: "",
            quantity: "",
            inventory_source: inventorySource,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error("API Error:", errorData);
        return;
      }

      const result = await response.json();
      console.log("Allocation Success:", result);
      // Optionally show toast or update UI
    } catch (error) {
      console.error("Fetch Error:", error);
    }
  };



  useEffect(() => {
    if (rotationalProducts?.warehouse_details) {
      const mapped = rotationalProducts.warehouse_details?.map((w) => ({
        warehouseId: w.warehouse._id,
        label: w.warehouse.label,
        bin_location: w.bin_location || "",
        available: w.available || 0,
      }));
      setEditableWarehouses(mapped);
      console.log("Mapped Warehouses:", mapped);
    }
  }, [rotationalProducts]);

  const handleGenerateSerialization = async () => {
    const { select_warehouse_id, serialize_quantity, serial_prefix } = formik.values;

    if (!select_warehouse_id || !serialize_quantity) {
      toast.error("Please select a warehouse and enter a quantity.");
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/signage/${userId}/rotational-products/${productId}/generate-serialization`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            warehouse_id: select_warehouse_id,
            quantity: Number(serialize_quantity),
            serial_prefix: serial_prefix || "",
          }),
        }
      );

      const result = await response.json();
      navigate(0);

      if (!response.ok) {
        toast.error(result.message || "Serialization failed.");
        return;
      }

      toast.success("Serialization generated successfully.");
      // Optionally refresh the product
    } catch (error) {
      console.error("Error generating serialization:", error);
      toast.error("Unexpected error occurred.");
    }
  };


  const fetchWarehouseSerializationData = async (warehouseId: string) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/signage/${userId}/rotational-products/${productId}/warehouses/${warehouseId}/serial-count`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await res.json();
      console.log("Serialization data:", data);
      if (!res.ok) {
        toast.error(data.message || "Failed to fetch warehouse serialization data");
        return;
      }

      setWarehouseTotals({
        total: data.available,
        serialized: data.serialized_unit_count,
        available: data.available - data.serialized_unit_count,
      });
    } catch (err) {
      console.error("Serialization fetch error", err);
      toast.error("Error getting warehouse serialization data");
    }
  };

    const warehouseMap = useMemo(() => {
      const map: Record<string, string> = {};
      warehouses.forEach((w) => {
        map[w._id] = w.label;
      });
      return map;
    }, [warehouses]);



  //   if (!isLoaded) return <div>Loading...</div>;

  return (
    <div className="teammember">
      <div className="d-flex  justify-content-between align-items-center border-bottom pb-3 page-container">
        <h4 className="mb-0 fw-bold">View Asset Product</h4>

        <div className="d-flex gap-1 align-items-center">

          <Link
            to={`${baseRoute}/editRotationalProduct/${productId}`}
            className="border-0 p-0"
            id={`action-edit-${productId}`}
            title="Edit"
            style={{ width: "36px", height: "36px" }}
          >
            <i className="ri-pencil-fill text-primary fs-24"></i>
          </Link>

        </div>
      </div>

      <div className="row">
        <div className="col-md-12 col-lg-12 col-xl-12">
          <div className="card mt-1 card-bg-fill">
            <div className="card-body p-4">
              <ul className="nav nav-pills nav-success mb-3 gap-2 flex-wrap" role="tablist">
                <li className="nav-item">
                  <a
                    className="activetabs active"
                    data-bs-toggle="tab"
                    href="#product-details"
                    role="tab"
                  >
                    Product Details
                  </a>
                </li>
                <li className="nav-item waves-effect waves-light">
                  <a
                    className="activetabs"
                    data-bs-toggle="tab"
                    href="#images"
                    role="tab"
                  >
                    Images
                  </a>
                </li>
                <li className="nav-item waves-effect waves-light">
                  <a
                    className="activetabs"
                    data-bs-toggle="tab"
                    href="#serialization"
                    role="tab"
                  >
                    Serialization
                  </a>
                </li>
                {/* <li className="nav-item waves-effect waves-light">
                        <a
                          className="nav-link"
                          data-bs-toggle="tab"
                          href="#agents"
                          role="tab"
                        >
                          Agents
                        </a>
                      </li> */}
                {/* <li className="nav-item waves-effect waves-light">
                        <a
                          className="nav-link"
                          data-bs-toggle="tab"
                          href="#offices"
                          role="tab"
                        >
                          Offices
                        </a>
                      </li> */}
              </ul>
              <div className="tab-content text-muted">
                <div
                  className="tab-pane active"
                  id="product-details"
                  role="tabpanel"
                >
                  <div className="d-flex mt-2">
                    <form onSubmit={formik.handleSubmit}>
                      <div className="row">
                        <div className="col-md-8">
                          <div className="row">
                            <div className="col-md-8 mb-3">
                              <label className="form-label">
                                Product Name{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                disabled
                                type="text"
                                className="form-control"
                                id="name"
                                name="name"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.name}
                              />
                              {formik.touched.name && formik.errors.name ? (
                                <div className="text-danger">
                                  {formik.errors.name}
                                </div>
                              ) : null}
                            </div>
                            <div className="col-md-4 mb-3">
                              <label className="form-label">
                                SKU <span className="text-danger">*</span>
                              </label>
                              <input
                                disabled
                                type="text"
                                className="form-control"
                                id="sku"
                                name="sku"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.sku}
                              />
                              {formik.touched.sku && formik.errors.sku ? (
                                <div className="text-danger">
                                  {formik.errors.sku}
                                </div>
                              ) : null}
                            </div>
                          </div>

                          <div className="row">
                            <div className="col-md-8">
                              <div className="">
                                <div className="">
                                  {/* <h5>Branding</h5> */}
                                  <div className="col-md-12 mb-3">
                                    <label className="form-label">Brand</label>
                                    <select
                                      disabled
                                      className="form-control"
                                      id="brand"
                                      name="brand"
                                      onChange={formik.handleChange}
                                      onBlur={formik.handleBlur}
                                      value={formik.values.brand}
                                    >
                                      <option value="">- Select -</option>
                                      {brands !== null &&
                                        brands?.map((brand) => (
                                          <option value={brand.name}>
                                            {brand.name}
                                          </option>
                                        ))}
                                    </select>
                                    {formik.touched.brand &&
                                      formik.errors.brand ? (
                                      <div className="text-danger">
                                        {formik.errors.brand}
                                      </div>
                                    ) : null}
                                  </div>
                                  <div className="col-md-12 mb-3">
                                    <label className="form-label">
                                      Category
                                    </label>
                                    <select
                                      disabled
                                      className="form-control"
                                      id="category"
                                      name="category"
                                      onChange={formik.handleChange}
                                      onBlur={formik.handleBlur}
                                      value={formik.values.category}
                                    >
                                      <option value="">- Select -</option>
                                      {categories !== null &&
                                        categories?.map((category) => (
                                          <option value={category.name}>
                                            {category.name}
                                          </option>
                                        ))}
                                    </select>
                                    {formik.touched.category &&
                                      formik.errors.category ? (
                                      <div className="text-danger">
                                        {formik.errors.category}
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-md-4">
                              <div className="">
                                <div className="">
                                  {/* <h5>Pricing</h5> */}
                                  <div className="col-md-12 mb-3">
                                    <label className="form-label">
                                      Purchase Price{" "}
                                    </label>
                                    <input
                                      disabled
                                      type="text"
                                      className="form-control"
                                      id="purchase_price"
                                      name="purchase_price"
                                      onChange={formik.handleChange}
                                      onBlur={formik.handleBlur}
                                      value={formik.values.purchase_price}
                                    />
                                    {/* {formik.touched.purchase_price &&
                                      formik.errors.purchase_price ? (
                                      <div className="text-danger">
                                        {formik.errors.purchase_price}
                                      </div>
                                    ) : null} */}
                                  </div>
                                  <div className="col-md-12 mb-4">
                                    <div className="mt-4">
                                      <label className="form-label ">
                                        Retail Price{" "}
                                        <span className="text-danger">*</span>
                                      </label>
                                      <input
                                        disabled
                                        type="text"
                                        className="form-control"
                                        id="retail_price"
                                        name="retail_price"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.retail_price}
                                      />
                                      {formik.touched.retail_price &&
                                        formik.errors.retail_price ? (
                                        <div className="text-danger">
                                          {formik.errors.retail_price}
                                        </div>
                                      ) : null}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="col-md-12 mb-3">
                              <label className="form-label">
                                Product Description{" "}
                              </label>
                              <textarea
                                disabled
                                className="form-control"
                                id="description"
                                name="description"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.description}
                                rows={4}
                              ></textarea>
                              {formik.touched.description &&
                                formik.errors.description ? (
                                <div className="text-danger">
                                  {formik.errors.description}
                                </div>
                              ) : null}
                            </div>
                            <div className="mb-3">
                              <div className="col-md-6">
                                <div className="table-responsive" style={{ maxHeight: "300px", overflowY: "auto" }}>
                                  <table className="table table-bordered table-sm align-middle">
                                    <thead className="table-light" style={{ position: "sticky", top: 0 }}>
                                      <tr>
                                        <th className="text-center table-headers" style={{ width: "30%" }}>Warehouse</th>
                                        <th className="text-center table-headers" style={{ width: "20%" }}>Available Quantity</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {currentWarehouses.map((wh) => {
                                        const matching = rotationalProducts?.warehouse_details?.find(
                                          (wd) => wd.warehouse._id === wh._id
                                        );

                                        const availableQty =
                                          editableWarehouses.find((item) => item.warehouseId === wh._id)?.available ?? 0;

                                        return (
                                          <tr key={wh._id}>
                                            <td className="table-cells text-center fw-bold">{wh.label}</td>
                                            <td className="table-cells text-center fw-bold">{availableQty}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>

                                  </table>
                                </div>
                              </div>
                            </div>


                            <div className="row" id="row-overlap">
                              <div className="col-md-12">
                                <div className="">
                                  <div className="">
                                    <div className="row">
                                      {/* <div className="col-md-4 mb-3 ms-0 me-0">
                                        <label className="form-label">
                                          Threshold Quantity{" "}
                                        </label>
                                        <input
                                          type="number"
                                          className="form-control"
                                          id="threshold_qty"
                                          name="threshold_qty"
                                          onChange={formik.handleChange}
                                          onBlur={formik.handleBlur}
                                          value={formik.values.threshold_qty}
                                        />
                                        {formik.touched.threshold_qty &&
                                          formik.errors.threshold_qty ? (
                                          <div className="text-danger">
                                            {formik.errors.threshold_qty}
                                          </div>
                                        ) : null}
                                      </div>
                                      <div className="col-md-4 mb-3">
                                        <label className="form-label">
                                          Is Rotational{" "}
                                          <span className="text-danger">*</span>
                                        </label>
                                        <select
                                          className="form-control"
                                          id="is_rotational"
                                          name="is_rotational"
                                          onChange={formik.handleChange}
                                          onBlur={formik.handleBlur}
                                          value={formik.values.is_rotational}
                                        >
                                          <option value="">- Select -</option>
                                          <option value="yes">Yes</option>
                                          <option value="no">No</option>
                                        </select>
                                        {formik.touched.is_rotational &&
                                          formik.errors.is_rotational ? (
                                          <div className="text-danger">
                                            {formik.errors.is_rotational}
                                          </div>
                                        ) : null}
                                      </div>
                                      <div className="col-md-4 mb-3">
                                        <label className="form-label">
                                          Is Production
                                          <span className="text-danger">*</span>
                                        </label>
                                        <select
                                          className="form-control"
                                          id="is_production"
                                          name="is_production"
                                          onChange={formik.handleChange}
                                          onBlur={formik.handleBlur}
                                          value={formik.values.is_production}
                                        >
                                          <option value="">- Select -</option>
                                          <option value="yes">Yes</option>
                                          <option value="no">No</option>
                                        </select>
                                        {formik.touched.is_production &&
                                          formik.errors.is_production ? (
                                          <div className="text-danger">
                                            {formik.errors.is_production}
                                          </div>
                                        ) : null}
                                      </div> */}
                                      {/* <div className="col-md-3 mb-3">
                                        <label className="form-label">
                                          Status{" "}
                                          <span className="text-danger">*</span>
                                        </label>
                                        <select
                                          disabled
                                          className="form-control"
                                          id="statuss"
                                          name="statuss"
                                          onChange={formik.handleChange}
                                          onBlur={formik.handleBlur}
                                          value={formik.values.statuss}
                                        >
                                          <option value="">- Select -</option>
                                          <option value="Active">Active</option>
                                          <option value="Inactive">
                                            Inactive
                                          </option>
                                        </select>
                                        {formik.touched.statuss &&
                                          formik.errors.statuss ? (
                                          <div className="text-danger">
                                            {formik.errors.statuss}
                                          </div>
                                        ) : null}
                                      </div> */}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="col-md-4">
                          <div className="col-md-12">
                            <div className="card">
                              <div className="card-body">
                                <div className="col-md-12 mt-3">
                                  <label className="form-label ms-2">
                                    Dimensions/Measurements (Inches)
                                  </label>
                                  <div className="row">
                                    <div className="col-md-4 mb-3">
                                      <input
                                        disabled
                                        type="text"
                                        className="form-control"
                                        id="length"
                                        name="length"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.length}
                                        placeholder="Length"
                                      />
                                      {formik.touched.length &&
                                        formik.errors.length ? (
                                        <div className="text-danger">
                                          {formik.errors.length}
                                        </div>
                                      ) : null}
                                    </div>
                                    <div className="col-md-4 mb-3">
                                      <input
                                        disabled
                                        type="text"
                                        className="form-control"
                                        id="height"
                                        name="height"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.height}
                                        placeholder="Height"
                                      />
                                      {formik.touched.height &&
                                        formik.errors.height ? (
                                        <div className="text-danger">
                                          {formik.errors.height}
                                        </div>
                                      ) : null}
                                    </div>
                                    <div className="col-md-4 mb-3">
                                      <input
                                        disabled
                                        type="text"
                                        className="form-control"
                                        id="width"
                                        name="width"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.width}
                                        placeholder="Width"
                                      />
                                      {formik.touched.width &&
                                        formik.errors.width ? (
                                        <div className="text-danger">
                                          {formik.errors.width}
                                        </div>
                                      ) : null}
                                    </div>
                                    <div className="col-md-12 mb-3">
                                      <label className="form-label">
                                        Weight(in gms)
                                      </label>
                                      <input
                                        disabled
                                        type="text"
                                        className="form-control"
                                        id="weight"
                                        name="weight"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.weight}
                                      />
                                      {formik.touched.weight &&
                                        formik.errors.weight ? (
                                        <div className="text-danger">
                                          {formik.errors.weight}
                                        </div>
                                      ) : null}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-12">
                            <div className="card">
                              <div className="card-body">
                                <div className="col-md-12 mb-3">
                                  <label className="form-label">
                                    Item Condition
                                  </label>
                                  <select
                                    disabled
                                    className="form-control"
                                    id="item_condition"
                                    name="item_condition"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.item_condition}
                                  >
                                    <option value="">- Select -</option>
                                    <option value="new">New</option>
                                    <option value="used">Used</option>
                                    <option value="damaged">Damaged</option>
                                  </select>
                                  {formik.touched.item_condition &&
                                    formik.errors.item_condition ? (
                                    <div className="text-danger">
                                      {formik.errors.item_condition}
                                    </div>
                                  ) : null}
                                </div>
                                <div className="col-md-12 mb-3">
                                  <label className="form-label">
                                    Condition Notes{" "}
                                  </label>
                                  <textarea
                                    disabled
                                    className="form-control"
                                    id="condition_note"
                                    name="condition_note"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.condition_note}
                                    rows={3}
                                  ></textarea>
                                  {formik.touched.condition_note &&
                                    formik.errors.condition_note ? (
                                    <div className="text-danger">
                                      {formik.errors.condition_note}
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          </div>
                          {/* <div className="col-md-12">
                            <div className="card">
                              <div className="card-body">
                                <div className="col-md-12 mb-3">
                                  <label className="form-label ms-2 me-3">Is Taxable</label>
                                  <input
                                    type="checkbox"
                                    name="is_taxable"
                                    id="is_taxable"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    checked={formik.values.is_taxable}
                                  />
                                </div>

                                <div className="col-md-12 mb-3">
                                  <label className="form-label">Tax Rule</label>
                                  <select
                                    className="form-control"
                                    id="tax_rule_id"
                                    name="tax_rule_id"
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      formik.setFieldValue("tax_rule_id", value); // Save full value like 'group-123' or 'rule-456'

                                      if (value.startsWith("rule-")) {
                                        const ruleId = value.replace("rule-", "");
                                        const selectedRule = taxRules?.find((rule) => rule._id === ruleId);
                                        if (selectedRule) {
                                          formik.setFieldValue("taxRule", selectedRule);
                                        }
                                      } else if (value.startsWith("group-")) {
                                        const groupId = value.replace("group-", "");
                                        const selectedGroup = taxGroups?.find((group) => group.id === groupId);
                                        if (selectedGroup) {
                                          formik.setFieldValue("taxGroup", selectedGroup); // Optional: store selected group if needed
                                        }
                                      }
                                    }}

                                    onBlur={formik.handleBlur}
                                    value={formik.values.tax_rule_id}
                                    disabled={!formik.values.is_taxable} // Disable if product is not taxable
                                  >
                                    <option value="">- Select -</option>
                                    {taxGroups &&
                                      taxGroups?.map((group) => (
                                        <option key={`group-${group.id}`} value={`group-${group.id}`} style={{ fontWeight: "bold" }}>
                                          {group.name}
                                        </option>

                                      ))}
                                    {taxRules &&
                                      taxRules?.map((rule) => (
                                        <option key={`rule-${rule._id}`} value={`rule-${rule._id}`}>
                                          {`${rule.country} - ${rule.taxRate}%`}
                                        </option>

                                      ))}
                                  </select>
                                  {formik.touched.tax_rule_id && formik.errors.tax_rule_id ? (
                                    <div className="text-danger">{formik.errors.tax_rule_id}</div>
                                  ) : null}
                                </div>

                              </div>
                            </div>
                          </div> */}

                          {/* <div className="col-md-12">
                            <div className="card">
                              <div className="card-body">
                                <div className="col-md-12 mb-3">
                                  <div className="row">
                                    <div className="col-md-12 mb-3">
                                      <label className="form-label">
                                        Image{" "}
                                      </label>
                                      <input
                                        type="file"
                                        className="form-control"
                                        id="image"
                                        name="image"
                                        onChange={(event) => {
                                          const file =
                                            event.currentTarget.files?.[0];
                                          if (file) {
                                            formik.setFieldValue("image", file);
                                            setPreview(
                                              URL.createObjectURL(file)
                                            ); // Optional: show preview
                                          }
                                        }}
                                        onBlur={formik.handleBlur}
                                      />
                                      {preview && (
                                        <img
                                          src={preview}
                                          alt="Preview"
                                          style={{
                                            maxWidth: "100px",
                                            marginTop: "10px",
                                          }}
                                        />
                                      )}
                                      {formik.touched.image &&
                                        formik.errors.image ? (
                                        <div className="text-danger">
                                          {formik.errors.image}
                                        </div>
                                      ) : null}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div> */}
                        </div>
                        {/* <div className="mb-3">
                          <div className="col-md-12">
                            <table className="white-table">
                              <thead>
                                <tr>
                                  <th>Warehouse</th>
                                  <th>Available Quantity</th>
                                </tr>
                              </thead>
                              <tbody>
                                {currentWarehouses.map((wh) => {
                                  const matching = rotationalProducts?.warehouse_details?.find(
                                    (wd) => wd.warehouse._id === wh._id
                                  );

                                  return (
                                    <tr key={wh._id}>
                                      <td>
                                        <input
                                          type="text"
                                          className="form-control"
                                          value={wh.label}
                                          disabled
                                        />
                                      </td>
                                      <td>
                                        <input
                                          type="number"
                                          className="form-control"
                                          placeholder="Available Quantity"
                                          value={
                                            editableWarehouses.find((item) => item.warehouseId === wh._id)?.available || 0
                                          }
                                          onChange={(e) => {
                                            const newList = [...editableWarehouses];
                                            const index = newList.findIndex((item) => item.warehouseId === wh._id);
                                            if (index !== -1) {
                                              newList[index].available = Number(e.target.value);
                                            } else {
                                              newList.push({
                                                warehouseId: wh._id,
                                                label: wh.label,
                                                available: Number(e.target.value),
                                              });
                                            }
                                            setEditableWarehouses(newList);
                                          }}
                                          disabled
                                        />
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>

                            </table>
                            <div className="d-flex justify-content-center mt-3">
                              {[...Array(totalPages)].map((_, index) => {
                                const page = index + 1;
                                return (
                                  <div
                                    key={page}
                                    className={`btn btn-sm me-2 ${page === currentPage ? "btn-primary" : "btn-outline-secondary"}`}
                                    onClick={() => setCurrentPage(page)}
                                  >
                                    {page}
                                  </div>
                                );
                              })}
                            </div>

                          </div>
                        </div> */}
                      </div>
                      <div className="row mb-3 rcust-margin-4">
                        <div className="col-md-12 d-flex justify-content-between">
                          <button
                            className="btn btn-outline-secondary br-8 backbutton ms-4"
                            type="button"
                            onClick={() => navigate(-1)}
                          >
                            Back
                          </button>
                          <button
                            className="btn btn-primary"
                            type="submit"
                          >
                            Save Product
                          </button>
                        </div>
                      </div>
                      <div className="mt-4 d-flex justify-content-end align-items-center">

                      </div>
                    </form>
                  </div>
                </div>

                <div className="tab-pane" id="images" role="tabpanel">
                  <div className="d-flex mt-2">
                    <div className="card">
                      {/* <div >
                        <div
                          onClick={() => fileInputRef.current?.click()} // ✅ safe access
                          className="imageuploadui"
                        >
                          <i className="fa-solid fa-cloud-arrow-up fa-2x mb-2"></i>
                          <p className="mb-0 fw-medium">Browse File to upload!</p>
                        </div>
                        <input
                          type="file"
                          ref={fileInputRef}
                          style={{ display: "none" }}
                          multiple
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            const newPreviews = files.map((file) => ({
                              file,
                              url: URL.createObjectURL(file),
                            }));
                            setPreviewFiles((prev) => [...prev, ...newPreviews]);
                          }}
                        />
                      </div> */}
                      <div className="row mt-3">
                        {uploadedFileMeta?.map((file, index) => (
                          <div key={index} className="col-md-3 position-relative mb-3">
                            <div
                              className="position-absolute top-0 end-0 bg-danger text-white rounded-circle imagepreview"
                              onClick={() => {
                                setUploadedFileMeta((prev) => prev.filter((_, i) => i !== index));
                              }}
                            >
                              &times;
                            </div>
                            <div className="border p-2">
                              <img
                                src={`${process.env.REACT_APP_BASE_URL}/${file.url?.replace(/^https?:\/\/[^\/]+\/(uploads\/.*)/, "$1")}`}
                                alt={file.alt_text}
                                className="img-fluid"
                                style={{ height: "150px", objectFit: "cover", width: "100%" }}
                              />
                              <div className="small text-center mt-1 text-muted">{file.alt_text}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="tab-pane"
                  id="serialization"
                  role="tabpanel"
                >
                  <div className="row mt-5">
                    <div className="col-md-12" id="row-overlap">
                      <div className="">
                        <div className="">
                          <h5 className="mb-2 ms-3">Serialization – Assets Product</h5>
                        </div>
                        <div className="card-body">
                          {/* Warehouse Selector */}
                          <div className="mb-3 col-md-3">
                            <label className="form-label mt-2 mb-2">Select Warehouse</label>
                            <select
                              className="form-control"
                              id="select_warehouse_id"
                              name="select_warehouse_id"
                              value={formik.values.select_warehouse_id}
                              onChange={(e) => {
                                formik.handleChange(e);
                                fetchWarehouseSerializationData(e.target.value);
                              }}
                            >
                              <option value="" disabled>Select Warehouse</option>
                              {warehouses?.map((w) => (
                                <option key={w._id} value={w._id}>
                                  {w.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Totals Row */}
                          <div className="row mb-3 text-center">
                            <div className="col-md-4">
                              <div className="bg-light p-3 rounded border">
                                <h6 className="mb-1 text-muted">Total Quantity</h6>
                                <h5 className="mb-0">{warehouseTotals.total}</h5>
                              </div>
                            </div>
                            <div className="col-md-4">
                              <div className="bg-light p-3 rounded border">
                                <h6 className="mb-1 text-muted">Serialized</h6>
                                <h5 className="mb-0">{warehouseTotals.serialized}</h5>
                              </div>
                            </div>
                            <div className="col-md-4">
                              <div className="bg-light p-3 rounded border">
                                <h6 className="mb-1 text-muted">Available for Serialization</h6>
                                <h5 className="mb-0">{warehouseTotals.available}</h5>
                              </div>
                            </div>
                          </div>

                          {/* Serialized Units Table */}
                          <div className="table-responsive mt-4">
                            <div className="fixed-header-table">
                              <table className="table table-bordered align-middle">
                                <thead className="table-light">
                                  <tr>
                                    <th>S.No</th>
                                    <th>Warehouse</th>
                                    <th>Serial Number</th>
                                    <th>Order Number</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {rotationalProducts?.serialized_units?.length ? (
                                    rotationalProducts.serialized_units.map((item: serializedUnits, index: number) => (
                                      <tr key={item.serial_id || index}>
                                        <td className="text-center table-cells">{index + 1}</td>
                                        <td className="text-center table-cells">
                                          {warehouseMap[item.storage_location?.warehouse_id] || "-"}
                                        </td>
                                        <td className="text-center table-cells">{item.serial_code}</td>
                                        <td className="text-center table-cells">{item.order_number || "-"}</td>
                                      </tr>
                                    ))
                                  ) : (
                                    <tr>
                                      <td colSpan={4} className="text-center">
                                        No data available
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>

                          </div>
                        </div>
                      </div>
                    </div>
                  </div>


                  {/* <div className="tab-pane" id="agents" role="tabpanel">
                        <div className="row mb-4">
                          <table className="table table-bordered">
                            <thead>
                              <tr className="bg-info text-white">
                                <th>Agent</th>
                                <th>Warehouse</th>
                                <th>Allocated Quantity</th>
                                <th>Available Quantity</th>
                                <th>Link Actions</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {agentLinkedProducts?.map((item, index) => (
                                <tr key={index}>
                                  <td>
                                    <Select
                                      className="form-control"
                                      options={agentOptions}
                                      value={agentOptions.find(
                                        (opt) => opt.value === item.agentId
                                      )}
                                      onChange={(selected) =>
                                        handleAgentChange(index, selected)
                                      }
                                      noOptionsMessage={() =>
                                        "Agent will show after creating product"
                                      }
                                    />
                                  </td>
                                  <td>
                                    <Select
                                      className="form-control"
                                      options={warehouseOptions}
                                      value={warehouseOptions.find(
                                        (opt) => opt.value === item.warehouseId
                                      )}
                                      onChange={(selected) =>
                                        handleAgentWarehouseChange(
                                          index,
                                          selected
                                        )
                                      }
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="AllocatedQty"
                                      value={item.allocatedQty}
                                      readOnly
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      className="form-control"
                                      value={item.availableQty}
                                      onChange={(e) =>
                                        handleAgentAvailableChange(
                                          index,
                                          Number(e.target.value)
                                        )
                                      }
                                    />
                                  </td>
                                  <td>
                                    <div className="row mb-3 mt-3">
                                      <div className="col-md-6">
                                        <input type="checkbox" /> With Supplier
                                      </div>
                                      <div className="col-md-6">
                                        <input type="checkbox" /> Linked
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <button
                                      type="button"
                                      className="btn btn-danger"
                                      onClick={() =>
                                        removeAgentLinkedProduct(index)
                                      }
                                    >
                                      Remove
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
  
                          <button
                            type="button"
                            className="btn btn-primary mt-1 col-md-3"
                            onClick={addAgentLinkedProduct}
                          >
                            Link Rotation Product
                          </button>
                          <br />
                          <button
                            className="btn btn-success my-3"
                            onClick={() => allocateQuantity("agent")}
                          >
                            Submit
                          </button>
                        </div>
                      </div> */}

                  {/* <div className="tab-pane" id="offices" role="tabpanel">
                        <div className="row mb-4">
                          <table className="table table-bordered">
                            <thead>
                              <tr className="bg-info text-white">
                                <th>Office</th>
                                <th>Warehouse</th>
                                <th>Allocated Quantity</th>
                                <th>Available Quantity</th>
                                <th>Link Actions</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {officeLinkedProducts?.map((item, index) => (
                                <tr key={index}>
                                  <td>
                                    <Select
                                      className="form-control"
                                      options={officeOptions}
                                      value={officeOptions.find(
                                        (opt) => opt.value === item.officeId
                                      )}
                                      onChange={(selected) =>
                                        handleOfficeChange(index, selected)
                                      }
                                      noOptionsMessage={() =>
                                        "Office will show after creating product"
                                      }
                                    />
                                  </td>
                                  <td>
                                    <Select
                                      className="form-control"
                                      options={warehouseOptions}
                                      value={warehouseOptions.find(
                                        (opt) => opt.value === item.warehouseId
                                      )}
                                      onChange={(selected) =>
                                        handleOfficeWarehouseChange(
                                          index,
                                          selected
                                        )
                                      }
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="Allocated Qty"
                                      value={item.allocatedQty}
                                      readOnly
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      className="form-control"
                                      value={item.availableQty}
                                      onChange={(e) =>
                                        handleOfficeAvailableChange(
                                          index,
                                          Number(e.target.value)
                                        )
                                      }
                                    />
                                  </td>
                                  <td>
                                    <div className="row mb-3 mt-3">
                                      <div className="col-md-6">
                                        <input type="checkbox" /> With Supplier
                                      </div>
                                      <div className="col-md-6">
                                        <input type="checkbox" /> Linked
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <button
                                      type="button"
                                      className="btn btn-danger"
                                      onClick={() =>
                                        removeOfficeLinkedProduct(index)
                                      }
                                    >
                                      Remove
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
  
                          <button
                            type="button"
                            className="btn btn-primary mt-1 col-md-3"
                            onClick={addOfficeLinkedProduct}
                          >
                            Link Rotation Product
                          </button>
                          <br />
                          <button
                            className="btn btn-success my-3"
                            onClick={() => allocateQuantity("office")}
                          >
                            Submit
                          </button>
                        </div>
                      </div> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignageViewRotationalProduct;
